import React, { useState, useEffect } from 'react';
import { clinicService } from '../../../services/api';
import dayjs from 'dayjs';

const weekDays = [
  { value: "0", label: 'Mon' },
  { value: "1", label: 'Tue' },
  { value: "2", label: 'Wed' },
  { value: "3", label: 'Thu' },
  { value: "4", label: 'Fri' },
  { value: "5", label: 'Sat' },
  { value: "6", label: 'Sun' },
  { value: "PH", label: 'PH' }
];

const formatScheduleDisplay = (schedule) => {
  if (!schedule) return null;
  return weekDays.map(day => {
    const slots = schedule[day.value] || [];
    if (slots.length === 0) return { day: day.label, text: '-' };
    const slotStr = slots.map(s => `${s.from}-${s.to}`).join(', ');
    return { day: day.label, text: slotStr };
  });
};

const ScheduleTable = ({ schedules, title }) => {
  if (!schedules || schedules.length === 0) {
    return (
      <div>
        <h4 className="font-medium text-gray-700 mb-2">{title}</h4>
        <p className="text-sm text-gray-500">No schedules</p>
      </div>
    );
  }
  return (
    <div>
      <h4 className="font-medium text-gray-700 mb-2">{title}</h4>
      <div className="space-y-2">
        {schedules.map((s, i) => (
          <div key={i} className="text-sm">
            {s.is_default_schedule ? (
              <span className="text-blue-600 font-medium">Default</span>
            ) : (
              <span className="text-gray-600">
                {s.from_date && dayjs(s.from_date).format('YYYY-MM-DD')} - {s.to_date && dayjs(s.to_date).format('YYYY-MM-DD')}
              </span>
            )}
            <div className="grid grid-cols-4 gap-1 mt-1 text-xs text-gray-600">
              {formatScheduleDisplay(s.schedule)?.map((d, j) => (
                <div key={j}><span className="font-medium">{d.day}:</span> {d.text}</div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const isEmptySchedule = (s) => {
  if (!s?.schedule) return true;
  return Object.keys(s.schedule).every((k) => !(s.schedule[k]?.length));
};

const Step4_Preview = ({ clinicId, scheduleForm, selectedDoctorIds, doctors, onBack, onNext, error, setError }) => {
  const [originalByDoctor, setOriginalByDoctor] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const results = {};
        for (const doctorId of selectedDoctorIds) {
          const data = await clinicService.getDoctorSchedules(clinicId, doctorId);
          results[doctorId] = data.schedules || [];
        }
        setOriginalByDoctor(results);
      } catch (err) {
        setError(err.response?.data?.detail || err.message || 'Failed to load schedules');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [clinicId, selectedDoctorIds, setError]);

  const newScheduleDisplay = {
    is_default_schedule: scheduleForm.is_default_schedule,
    from_date: scheduleForm.from_date,
    to_date: scheduleForm.to_date,
    schedule: scheduleForm.schedule
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin h-8 w-8 border-2 border-blue-600 border-t-transparent rounded-full mx-auto mb-2" />
        <p className="text-gray-600">Loading schedules...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Step 4: Preview</h2>
        <p className="text-gray-600">
          {isEmptySchedule(scheduleForm)
            ? 'Empty schedule = delete matching schedule for each doctor. Compare below.'
            : 'Compare original vs updated schedules before confirming.'}
        </p>
      </div>

      <div className="space-y-6">
        {doctors.map((doctor) => (
          <div key={doctor.id} className="border rounded-lg p-4 bg-white">
            <h3 className="font-semibold text-gray-900 mb-4">{doctor.primary_name}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-3 bg-gray-50 rounded border">
                <ScheduleTable
                  schedules={originalByDoctor[doctor.id] || []}
                  title="Original"
                />
              </div>
              <div className="p-3 bg-blue-50 rounded border border-blue-200">
                <ScheduleTable
                  schedules={isEmptySchedule(scheduleForm) ? [] : [newScheduleDisplay]}
                  title={isEmptySchedule(scheduleForm) ? 'Updated (matching schedule will be deleted)' : 'Updated (new schedule added)'}
                />
                {isEmptySchedule(scheduleForm) && (
                  <p className="text-sm text-amber-700 mt-2">No slots = delete matching schedule</p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">{error}</div>
      )}

      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 font-medium"
        >
          Back
        </button>
        <button
          onClick={() => onNext({ originalByDoctor })}
          className="px-8 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default Step4_Preview;
